import type { ZoneKindInput } from '@chukua/common';
import { Button, Table } from 'antd';
import { Plus, Tag as TagIcon } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../components/EmptyState';
import { TableSkeleton } from '../../components/TableSkeleton';
import { buildZoneKindColumns } from './build-zone-kind-columns';
import type { ZoneKind } from './reference-data.api';
import { ZoneKindFormDrawer } from './ZoneKindFormDrawer';

interface ZoneKindsPanelProps {
  kinds: ZoneKind[];
  loading: boolean;
  saveError: string | null;
  saving: boolean;
  onCreate: (input: ZoneKindInput) => Promise<boolean>;
  onUpdate: (id: string, input: ZoneKindInput) => Promise<boolean>;
  onDeactivate: (row: ZoneKind) => Promise<void>;
  onReactivate: (row: ZoneKind) => Promise<void>;
  onResetSaveError: () => void;
}

/** Client-side paging (Ant's default) — the small-fixed-catalogue exception to server-side lists (CLAUDE.md → Lists and tables). */
export function ZoneKindsPanel({ kinds, loading, saveError, saving, onCreate, onUpdate, onDeactivate, onReactivate, onResetSaveError }: ZoneKindsPanelProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<ZoneKind | undefined>(undefined);

  function openCreate() {
    onResetSaveError();
    setEditing(undefined);
    setDrawerOpen(true);
  }

  function openEdit(row: ZoneKind) {
    onResetSaveError();
    setEditing(row);
    setDrawerOpen(true);
  }

  const columns = buildZoneKindColumns({ onEdit: openEdit, onDeactivate, onReactivate });
  const newButton = (
    <Button type="primary" icon={<Plus className="size-4" />} onClick={openCreate}>
      New zone kind
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">{newButton}</div>
      {loading && <TableSkeleton label="Loading zone kinds" />}
      {!loading && kinds.length === 0 && (
        <EmptyState icon={TagIcon} title="No zone kinds yet" description="Seeded with the Index Cargo corridor's 13 — add more as this deployment needs them.">
          {newButton}
        </EmptyState>
      )}
      {!loading && kinds.length > 0 && <Table rowKey="id" dataSource={kinds} columns={columns} pagination={{ pageSize: 25 }} scroll={{ x: 'max-content' }} />}
      <ZoneKindFormDrawer
        open={drawerOpen}
        editing={editing}
        error={saveError}
        submitting={saving}
        onSubmit={(input) => (editing ? onUpdate(editing.id, input) : onCreate(input))}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
